import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { api, ApiError, qs } from "@/lib/api";
import { formatCents, formatDateTime } from "@/lib/format";
import { useIsAdmin } from "@/lib/session";
import { Empty } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Money requests from API and MCP tokens (go-live audit XC-H2, crm-billing: approvals_money_*). A refund or charge an
 * AI asks for waits here until a signed-in admin approves it; the approval email's button opens the detail page. The
 * server checks the details again before anything runs.
 */

export type MoneyStatus = "pending" | "running" | "done" | "failed" | "stale" | "declined" | "expired";

export interface MoneyApprovalRow {
  id: number;
  op: string;
  op_title: string;
  kind: string;
  summary: string;
  amount_cents: number | null;
  contact_id: number | null;
  customer?: string;
  reason: string;
  status: MoneyStatus;
  asked_by: string;
  requested_at: string;
  expires_at: string;
  decided_by: string | null;
  decided_at: string | null;
  finished_at: string | null;
  error: string | null;
}

interface Details {
  kind_label: string;
  summary: string;
  amount?: string;
  card?: string;
  asked_by: string;
  customer?: { contact_id: number; name: string; company?: string; emails: string[]; phones: string[]; addresses: string[] };
  payment?: { id: number; date: string; amount: string; paid_with: string; gateway: string; transaction_id?: string; refunded: string; refunds?: string[] };
  invoice?: { id: number; number: string; description?: string; status: string; due: string; paid: string; due_date?: string };
  orders?: { id: number; placed?: string; status: string; total: string; items: string[]; invoices?: string[] }[];
  subscription?: { id: number; product?: string; plan: string; status: string; next_bill?: string };
  facts?: [string, string][];
}

export interface MoneyApproval extends MoneyApprovalRow {
  what: string;
  details: Details;
  decision_note: string | null;
  result: unknown;
  message?: string;
}

export const MONEY_STATUS: Record<MoneyStatus, [string, "warning" | "success" | "muted" | "destructive" | "default"]> = {
  pending: ["Waiting for you", "warning"],
  running: ["Approved, running", "default"],
  done: ["Approved and done", "success"],
  failed: ["Approved, failed", "destructive"],
  stale: ["Closed: details changed", "muted"],
  declined: ["Declined", "muted"],
  expired: ["Expired", "muted"],
};

/** The list on the Approvals page: money requests from tokens. */
export function MoneyApprovalsSection({ filter }: { filter: "pending" | "all" }) {
  const q = useQuery({
    queryKey: ["money-approvals", filter],
    queryFn: () => api<{ approvals: MoneyApprovalRow[]; pending: number }>("/api/v1/money-approvals" + qs({ status: filter, limit: 100 })),
  });
  const items = q.data?.approvals ?? [];
  return (
    <section className="mb-4 space-y-2" aria-label="Refunds and charges asked for by AI or scripts">
      <h2 className="text-sm font-semibold">Refunds and charges asked for by AI or scripts</h2>
      <p className="text-sm text-[var(--color-muted-foreground)]">
        When Claude, Grok or a script asks to refund, charge or change what someone pays, nothing moves until you approve it here. Each one also arrives as an
        email.
      </p>
      {q.isLoading && <Skeleton className="h-16" />}
      {q.isError && (
        <Card>
          <Empty title="Couldn't load money requests">{(q.error as Error).message}</Empty>
        </Card>
      )}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <Card>
          <Empty title={filter === "pending" ? "No money requests waiting." : "No money requests yet."} />
        </Card>
      )}
      {items.length > 0 && (
        <Card className="divide-y">
          {items.map((a) => {
            const [label, variant] = MONEY_STATUS[a.status] ?? [a.status, "muted" as const];
            return (
              <Link
                key={a.id}
                to="/billing/money-approvals/$approvalId"
                params={{ approvalId: String(a.id) }}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm hover:bg-[var(--color-muted)]"
              >
                <Badge variant={variant}>{label}</Badge>
                <span className="font-medium">{a.summary || a.op_title}</span>
                <span className="text-[var(--color-muted-foreground)]">asked by {a.asked_by}</span>
                <span className="ml-auto text-xs text-[var(--color-muted-foreground)]">
                  {a.status === "pending" ? `expires ${formatDateTime(a.expires_at)}` : formatDateTime(a.decided_at ?? a.finished_at ?? a.requested_at)}
                </span>
              </Link>
            );
          })}
        </Card>
      )}
    </section>
  );
}

function Rows({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[minmax(110px,max-content)_1fr] gap-x-4 gap-y-1.5 px-4 py-3 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-[var(--color-muted-foreground)]">{k}</dt>
          <dd className="min-w-0 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

const none = (list?: string[]) => (list && list.length ? list.join(", ") : "none on file");

/** One request: every detail from the email, with Approve and Decline (the server checks again before it runs). */
export function MoneyApprovalPage() {
  const { approvalId } = useParams({ from: "/billing/money-approvals/$approvalId" });
  const id = Number(approvalId);
  const isAdmin = useIsAdmin();
  const q = useQuery({ queryKey: ["money-approval", id], queryFn: () => api<MoneyApproval>(`/api/v1/money-approvals/${id}`) });
  const [acting, setActing] = useState<"approve" | "decline" | null>(null);
  if (q.isLoading) return <Skeleton className="h-96" />;
  if (!q.data) return <p className="text-sm text-[var(--color-danger-fg)]">{q.error?.message ?? "Request not found."}</p>;
  const a = q.data;
  const d = a.details ?? ({} as Details);
  const [label, variant] = MONEY_STATUS[a.status] ?? [a.status, "muted" as const];
  const c = d.customer;
  return (
    <div className="mx-auto max-w-[900px] space-y-3">
      <Link to="/billing/approvals" className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
        <ArrowLeft className="size-4" /> Approvals
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{d.summary || a.op_title}</h1>
        <Badge variant={variant}>{label}</Badge>
      </div>
      <Card>
        <div className="space-y-2 px-4 py-3 text-sm">
          <p>
            <span className="font-medium">{a.asked_by}</span> asked for this on {formatDateTime(a.requested_at)}.{" "}
            {a.status === "pending" ? "Nothing has moved yet." : null}
          </p>
          <p>
            <span className="text-[var(--color-muted-foreground)]">Reason given: </span>
            <span className="font-medium">{a.reason}</span>
          </p>
          {a.status === "pending" && <p className="text-[var(--color-muted-foreground)]">Expires {formatDateTime(a.expires_at)} if nobody decides.</p>}
          {a.decided_by && (
            <p className="text-[var(--color-muted-foreground)]">
              {a.status === "declined" ? "Declined" : "Approved"} by {a.decided_by} on {formatDateTime(a.decided_at)}
              {a.decision_note ? ` (${a.decision_note})` : ""}.
            </p>
          )}
          {a.error && <p className="text-[var(--color-danger-fg)]">{a.error}</p>}
        </div>
        {a.status === "pending" &&
          (isAdmin ? (
            <div className="flex flex-wrap gap-2 border-t px-4 py-3">
              <Button onClick={() => setActing("approve")}>Approve</Button>
              <Button variant="outline" onClick={() => setActing("decline")}>
                Decline
              </Button>
            </div>
          ) : (
            <p className="border-t px-4 py-3 text-sm text-[var(--color-muted-foreground)]">Only an admin can approve or decline money requests.</p>
          ))}
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>What happens</CardTitle>
        </CardHeader>
        <Rows
          rows={[
            ["What", `${d.kind_label ?? a.kind} (${a.op_title})`],
            ...(d.amount ? ([["Amount", <span className="num font-medium">{d.amount}</span>]] as [string, ReactNode][]) : []),
            ...(d.card ? ([["Card to charge", d.card]] as [string, ReactNode][]) : []),
            ...((d.facts ?? []).map(([k, v]) => [k, v]) as [string, ReactNode][]),
            ["Confirm text", a.what],
          ]}
        />
      </Card>
      {c && (
        <Card>
          <CardHeader>
            <CardTitle>Customer</CardTitle>
            <Link to="/contacts/$contactId" params={{ contactId: String(c.contact_id) }} className="text-sm text-[var(--color-primary)] hover:underline">
              Open contact #{c.contact_id}
            </Link>
          </CardHeader>
          <Rows
            rows={[
              ["Name", c.name || "(no name)"],
              ...(c.company ? ([["Company", c.company]] as [string, ReactNode][]) : []),
              ["Emails", none(c.emails)],
              ["Phones", none(c.phones)],
              ["Mailing address", c.addresses?.length ? c.addresses.join("; ") : "none on file"],
            ]}
          />
        </Card>
      )}
      {d.payment && (
        <Card>
          <CardHeader>
            <CardTitle>Original payment #{d.payment.id}</CardTitle>
          </CardHeader>
          <Rows
            rows={[
              ["Date", d.payment.date],
              ["Amount", <span className="num">{d.payment.amount}</span>],
              ["Paid with", d.payment.paid_with],
              ["Gateway", d.payment.gateway],
              ...(d.payment.transaction_id ? ([["Gateway transaction", <span className="num">{d.payment.transaction_id}</span>]] as [string, ReactNode][]) : []),
              ["Already refunded", <span className="num">{d.payment.refunded}</span>],
              ...(d.payment.refunds?.length ? ([["Refunds", d.payment.refunds.join("; ")]] as [string, ReactNode][]) : []),
            ]}
          />
        </Card>
      )}
      {d.invoice && (
        <Card>
          <CardHeader>
            <CardTitle>Invoice {d.invoice.number}</CardTitle>
          </CardHeader>
          <Rows
            rows={[
              ["For", d.invoice.description || "(no description)"],
              ["Status", d.invoice.status],
              ["Amount due", <span className="num">{d.invoice.due}</span>],
              ["Paid so far", <span className="num">{d.invoice.paid}</span>],
              ...(d.invoice.due_date ? ([["Due date", d.invoice.due_date]] as [string, ReactNode][]) : []),
            ]}
          />
        </Card>
      )}
      {(d.orders ?? []).map((o) => (
        <Card key={o.id}>
          <CardHeader>
            <CardTitle>Order #{o.id}</CardTitle>
            <Link to="/billing/orders/$orderId" params={{ orderId: String(o.id) }} className="text-sm text-[var(--color-primary)] hover:underline">
              Open order
            </Link>
          </CardHeader>
          <Rows
            rows={[
              ["Placed", o.placed || "not placed"],
              ["Status", o.status],
              ["Total", <span className="num">{o.total}</span>],
              ["Bought", o.items.length ? o.items.join("; ") : "nothing listed"],
              ...(o.invoices?.length ? ([["Invoices", o.invoices.join(", ")]] as [string, ReactNode][]) : []),
            ]}
          />
        </Card>
      ))}
      {d.subscription && (
        <Card>
          <CardHeader>
            <CardTitle>Subscription #{d.subscription.id}</CardTitle>
          </CardHeader>
          <Rows
            rows={[
              ["Product", d.subscription.product || "(unknown)"],
              ["Plan now", d.subscription.plan],
              ["Status", d.subscription.status],
              ["Next bill date", d.subscription.next_bill || "none"],
            ]}
          />
        </Card>
      )}
      {acting && <DecideDialog a={a} action={acting} onClose={() => setActing(null)} />}
    </div>
  );
}

interface ApprovePreview {
  what_happens?: string;
  amount?: string;
  customer?: string;
}

/** Approve: the server's preview first (and its refusal if the request can't be approved), then confirm. Decline: one step. */
function DecideDialog({ a, action, onClose }: { a: MoneyApproval; action: "approve" | "decline"; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState("");
  const preview = useQuery({
    queryKey: ["money-approval-preview", a.id],
    enabled: action === "approve",
    retry: false,
    queryFn: async () => {
      try {
        await api(`/api/v1/money-approvals/${a.id}/approve`, { json: {} });
        throw new Error("The server didn't ask for a confirmation; check the request's status.");
      } catch (e) {
        if (e instanceof ApiError && e.code === "confirm_required") return (e.details ?? {}) as ApprovePreview;
        throw e;
      }
    },
  });
  const decide = useMutation({
    mutationFn: () =>
      action === "approve"
        ? api<MoneyApproval>(`/api/v1/money-approvals/${a.id}/approve`, { json: { note: note || undefined, confirm: true } })
        : api<MoneyApproval>(`/api/v1/money-approvals/${a.id}/decline`, { json: { note: note || undefined } }),
    onSuccess: (r) => {
      toast(r.message || (action === "approve" ? "Approved" : "Declined"));
      void qc.invalidateQueries({ queryKey: ["money-approval", a.id] });
      void qc.invalidateQueries({ queryKey: ["money-approvals"] });
      onClose();
    },
    onError: (e) => {
      toast(e.message, "error");
      void qc.invalidateQueries({ queryKey: ["money-approval", a.id] });
      void qc.invalidateQueries({ queryKey: ["money-approvals"] });
      onClose();
    },
  });
  const ready = action === "decline" || !!preview.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{action === "approve" ? "Approve this request?" : "Decline this request?"}</DialogTitle>
        <DialogDescription>
          {action === "approve"
            ? "It runs once, as you. The CRM checks first that nothing changed since it was asked; if anything did, nothing runs."
            : "Nothing runs. Whoever asked can see it was declined."}
        </DialogDescription>
        {action === "approve" && preview.isLoading && <Skeleton className="h-16" />}
        {action === "approve" && preview.isError && <p className="text-sm text-[var(--color-danger-fg)]">{(preview.error as Error).message}</p>}
        {ready && (
          <div className="space-y-3 text-sm">
            {preview.data?.what_happens && <p className="font-medium">{preview.data.what_happens}</p>}
            {a.amount_cents != null && action === "approve" && (
              <p>
                Amount: <span className="num font-medium">{formatCents(a.amount_cents)}</span>
              </p>
            )}
            <label className="block">
              <span className="mb-1 block text-xs text-[var(--color-muted-foreground)]">Note (optional, saved with the decision)</span>
              <input className="h-9 w-full rounded-md border bg-[var(--color-card)] px-2 text-sm" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button variant={action === "approve" ? "destructive" : "default"} onClick={() => decide.mutate()} disabled={decide.isPending}>
                {decide.isPending ? "Working…" : action === "approve" ? "Approve and run it" : "Decline"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
